/**
 * Analytics Utilities
 *
 * Event tracking helpers for the DataTable component.
 */

import { AnalyticsEventType, AnalyticsHook } from '../types';

/**
 * Create analytics event payload
 */
function createAnalyticsEvent(
  type: AnalyticsEventType,
  properties: Record<string, any> = {}
) {
  return {
    name: type,
    properties: {
      ...properties,
      component: 'DataTable',
      version: '1.0.0',
    },
    timestamp: Date.now(),
  };
}

/**
 * Safe analytics hook wrapper
 *
 * Ensures analytics errors don't break the table
 */
export function emitAnalytics(
  hook: AnalyticsHook | undefined,
  type: AnalyticsEventType,
  properties?: Record<string, any>
) {
  if (!hook) return;

  try {
    hook(type, properties);
  } catch (error) {
    console.warn('DataTable analytics error:', error);
  }
}

/**
 * Common analytics event creators
 */
export const analyticsEvents = {
  sort: (columnId: string, direction: string) =>
    createAnalyticsEvent('table.sort', { columnId, direction }),

  filter: (columnId: string, operator: string, hasValue: boolean) =>
    createAnalyticsEvent('table.filter', { columnId, operator, hasValue }),

  paginate: (page: number, pageSize: number) =>
    createAnalyticsEvent('table.paginate', { page, pageSize }),

  select: (count: number, mode: 'single' | 'multiple' | 'all') =>
    createAnalyticsEvent('table.select', { count, mode }),

  rowAction: (actionId: string, rowKey: string | number) =>
    createAnalyticsEvent('table.action.row', { actionId, rowKey }),

  bulkAction: (actionId: string, count: number) =>
    createAnalyticsEvent('table.action.bulk', { actionId, count }),

  errorRetry: (errorType: string) =>
    createAnalyticsEvent('table.error.retry', { errorType }),

  columnResize: (columnId: string, width: number) =>
    createAnalyticsEvent('table.column.resize', { columnId, width }),

  columnReorder: (from: number, to: number) =>
    createAnalyticsEvent('table.column.reorder', { from, to }),

  densityChange: (density: string) =>
    createAnalyticsEvent('table.density.change', { density }),
};
